import React, { useEffect, useRef, useState } from "react";
import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
} from "../../../components/ui/card";
import { EyeOff, Eye, X, Upload, ImagePlusIcon } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import Table from "../../../components/table/table-2";
import { columns, data } from "../../../data/admin/role/create-pharmacy";
import { createPharmacyFields } from "../../../config/role/create-pharmacy";
import { isFormValid } from "../../../utils/validators";
import FormRenderer from "../../../components/input-field/form-renderer";
import {
    createPharmacySignup,
    getAllPharmacy,
    getPharmacyById,
    changePharmacyStatus,
} from "../../../services/pharmacy/pharmacy-auth-service";
import { showSuccessToast, showErrorToast, } from "../../../utils/toast-utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ViewPharmacyDataByID from "../../../components/viewsModals/viewPharmacyDateBYId";


const CreatePharmacy = () => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState({});
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPasswordError, setShowPasswordError] = useState("");
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [pharmacySignupResponse, setPharmacySignupResponse] = useState(null);
    const [showSignupLoader, setShowSignupLoader] = useState(false);
    const fileInputRef = useRef(null);
    const [preview, setPreview] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(5);
    const [viewPharmacyLoading, setViewPharmacyLoading] = useState(null);
    const [viewPharmacyData, setViewPharmacyData] = useState(null);


    const pharmacyColumns = [
        { label: "Name", key: "ownerName" },
        { label: "Email", key: "email" },
        { label: "Mobile", key: "mobile" },
        { label: "Gender", key: "gender" },
        { label: "DOB", key: "dob" },
        {
            label: "Status",
            key: "isActive",
            render: (row) => (
                <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${row.isActive
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                        }`}
                >
                    {row.isActive ? "Active" : "Inactive"}
                </span>
            ),
        },

        {
            label: "Actions",
            key: "actions",
            render: (row) => (
                <div className="flex gap-2">
                    <button
                        onClick={() => handleView(row.id)}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                    >
                        View
                    </button>
                </div>
            ),
        },
        {
            key: "isActive",
            label: "Status",
            render: (row) => (
                <button
                    onClick={() => handleStatus(row)}
                    className={`rounded-full border h-[28px] w-[50px] text-xs font-semibold items-center flex transition-all duration-200 ${row.isActive
                        ? "border-blue-700 bg-blue-100 justify-end"
                        : "border-gray-400 bg-gray-100 justify-start"
                        }`}
                >
                    <span
                        className={`inline-block h-[22px] w-[22px] rounded-full transition-all duration-200 ${row.isActive ? "bg-blue-700" : "bg-gray-400"
                            }`}
                    ></span>
                </button>
            ),
        },
        {
            label: "Created",
            key: "created_at",

            render: (row) => <span>{new Date(row.created_at).toLocaleString()}</span>,
        },
    ];


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };


    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file && file.size < 5 * 1024 * 1024) {
            setForm((prev) => ({ ...prev, file }));
            setPreview(URL.createObjectURL(file));
        } else {
            alert("File size should be less than 5MB");
        }
    };

    const handleView = async (id) => {
        if (!id) return;

        setViewPharmacyLoading(true);

        try {
            const res = await getPharmacyById({ id });
            console.log(res);
            setViewPharmacyData(res);
        } catch (error) {
            console.log(error);
        } finally {
            setViewPharmacyLoading(false);
        }
    };

    const {
        data: allPharmacyDetails,
        isLoading: isPharmacyDetailsLoading,
        error: getPharmacyError,
    } = useQuery({
        queryKey: ["getAllPharmacy", currentPage, limit],
        queryFn: async () => {
            return await getAllPharmacy({ limit, page: currentPage });
        },
    });


    const handleCreatePharmacy = async () => {
        if (!password) {
            return setShowPasswordError("Please enter password");
        } else if (!confirmPassword) {
            return setShowPasswordError("Please confirm password");
        }

        if (password !== confirmPassword) {
            return setShowPasswordError("Please confirm password");
        }

        setShowPasswordError("");

        const formData = new FormData();
        Object.entries(form).forEach(([key, value]) => {
            if (key === "file") {
                if (value instanceof File) {
                    formData.append("file", value);
                }
            } else {
                formData.append(key, value ?? "");
            }
        });

        formData.append("role", "pharmacy");
        formData.append("password", password);

        setShowSignupLoader(true);

        for (const [key, value] of formData.entries()) {
            console.log(key, value);
        }

        try {
            const res = await createPharmacySignup(formData);

            if (res?.data?.status === 200 || res?.status === 200) {
                setPharmacySignupResponse(res.data);
                setForm({});
                setPassword("");
                setConfirmPassword("");
                showSuccessToast(res?.data?.message || res?.message);
            }
            queryClient.invalidateQueries({
                queryKey: ["getAllPharmacy"],
            });

        } catch (error) {
            console.log(error);
        } finally {
            setShowSignupLoader(false);
        }
    };


      const handleStatus = async (row) => {
        setShowSignupLoader(true);
    
        try {
          const res = await changePharmacyStatus({
            id: row.id,
            isActive: !row.isActive,
          });
    
          if (res?.status === 200 || res?.data?.status === 200) {
            showSuccessToast(
              res?.data?.message ||
              res?.data?.data?.message ||
              "Status updated successfully"
            );
    
            queryClient.invalidateQueries({
              queryKey: ["getAllPharmacy"],
            });
          } else {
            showErrorToast(
              res?.data?.message ||
              res?.data?.data?.message ||
              "Something went wrong"
            );
          }
        } catch (error) {
          console.log("Change pharmacy status error:", error);
    
          showErrorToast(
            error?.response?.data?.message ||
            error?.message ||
            "Failed to update pharmacy status"
          );
        } finally {
          setShowSignupLoader(false);
        }
      };

    return (
        <div className="p-2">
            <h2 className="text-xl md:text-2xl text-black font-bold mb-6 md:mb-8">
                Create Pharmacy
            </h2>

            <div className="grid grid-cols-1 gap-8">
                <Card className=" rounded-xl bg-white  border-none ">
                    <div className="w-full p-6 flex flex-col">

                        <div className="w-full">
                            <FormRenderer
                                fields={createPharmacyFields}
                                values={form}
                                setValues={setForm}
                            />
                        </div>
                        <div className="w-full grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2 mt-4 ">

                            <div className=" w-full flex col-span-2 md:col-span-1 flex-col gap-2 ">
                                <label className="font-semibold text-gray-700">Password</label>
                                <div className="relative ">
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        placeholder="Password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />

                                    <Button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        variant="admin.default"
                                        className="absolute text-blue-600 right-[1px] -translate-y-1/2  top-[22px] px-2"
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </Button>
                                </div>
                            </div>

                            <div className=" w-full flex col-span-2 md:col-span-1 flex-col gap-2 ">

                                <label className="font-semibold text-gray-700">
                                    Confirm Password
                                </label>

                                <div className="relative ">
                                    <Input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="Confirm Password"
                                        placeholder="Confirm Password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />

                                    <Button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        variant="admin.default"
                                        className="absolute text-blue-600 right-[1px] -translate-y-1/2  top-[22px] px-2"
                                    >
                                        {showConfirmPassword ? (
                                            <EyeOff size={18} />
                                        ) : (
                                            <Eye size={18} />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {showPasswordError && (
                            <div className="w-full text-red-600">{showPasswordError}</div>
                        )}
                        <div className="flex items-center gap-4 min-w-fit mt-4">
                            <h2 className="text-lg font-semibold text-slate-800 whitespace-nowrap">
                                Pharmacy Logo
                            </h2>

                            {preview && (
                                <button
                                    type="button"
                                    onClick={() => setPreview(null)}
                                    className="flex items-center gap-2 text-xs bg-green-50 text-green-700 px-3 py-1 rounded-lg hover:bg-green-100 transition"
                                >
                                    <ImagePlusIcon size={16} />
                                    Add New
                                </button>
                            )}

                            {/* Center - Small Upload / Preview */}
                            <div className="flex items-center justify-center">
                                {preview ? (
                                    <div className="relative w-20 h-20 rounded-lg overflow-hidden group border">
                                        <img
                                            src={preview}
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setPreview(null)}
                                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition"
                                        >
                                            <X className="size-3" />
                                        </button>
                                    </div>
                                ) : (
                                    <label className="flex items-center justify-center w-24 h-20 border border-dashed border-slate-300 rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 transition text-xs text-slate-500">
                                        <Upload className="size-4 mr-1" />
                                        Upload
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                        />
                                    </label>
                                )}
                            </div>
                        </div>

                        <div className="mt-3">
                            <Button
                                onClick={handleCreatePharmacy}
                                disabled={!isFormValid(createPharmacyFields, form)}
                                variant="admin.secondary">
                                Summit
                            </Button>
                        </div>
                    </div>
                </Card>

                {viewPharmacyData && (
                    <ViewPharmacyDataByID
                        viewPharmacyData={viewPharmacyData?.data?.data}
                        setViewPharmacyData={setViewPharmacyData}
                    />
                )}

                <Card className="rounded-xl border-none bg-white p-2">
                    <div className="flex px-4 md:px-8 items-center w-full justify-between">
                        <CardTitle className="text-md md:text-xl text-gray-600">Pharmacy List</CardTitle>


                        <div className="flex flex-col pt-2 gap-1">
                            <label className="font-semibold" htmlFor="limit">
                                Select Limit
                            </label>
                            <select
                                onChange={(e) => setLimit(e.target.value)}
                                className="w-[150px] p-2 border bg-gradient-to-r from-blue-600 to-purple-700 text-white font-semibold rounded-lg"
                                name=""
                                id="limit"
                            >
                                <option className="bg-purple-800 text-white" value="5">
                                    5
                                </option>
                                <option className="bg-purple-800 text-white" value="10">
                                    10
                                </option>
                                <option className="bg-purple-800 text-white" value="20">
                                    20
                                </option>
                                <option className="bg-purple-800 text-white" value="50">
                                    50
                                </option>
                                <option className="bg-purple-800 text-white" value="100">
                                    100
                                </option>
                            </select>
                        </div>
                    </div>

                    <CardContent className=" text-[#081a3a] text-2xl md:text-4xl font-bold">
                        <Table columns={pharmacyColumns} data={allPharmacyDetails?.data?.data?.rows} rowsPerPage={4} headerBg="bg-[#0a1f44]" headerText="text-white" />
                        <div className="w-full flex justify-center items-center py-4 gap-4">
                            <Button
                                onClick={() => setCurrentPage((prev) => prev - 1)}
                                disabled={currentPage === 1}
                                variant="admin.secondary"
                            >
                                prev
                            </Button>

                            <div>
                                Page {allPharmacyDetails?.data?.data?.currentPage} of{" "}
                                {allPharmacyDetails?.data?.data?.totalPages}
                            </div>

                            <Button
                                onClick={() => setCurrentPage((prev) => prev + 1)}
                                disabled={
                                    allPharmacyDetails?.data?.data?.totalPages === currentPage
                                }
                                variant="admin.secondary"
                            >
                                next
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div >
        </div >
    );
};

export default CreatePharmacy;
